import {
  ArrowPostJobFormState,
  PostJobPageFormState,
  ProjectViewProject,
  Task,
  ThreadContext,
} from '@freelancer/datastore/collections';
import { RoleApi } from 'api-typings/common/common';
import { ThreadTypeApi } from 'api-typings/messages/messages_types';
import { DevelopersPreference } from 'developers/developers-preference.service';

/**
 *  Holds the types which can be stored in local storage i.e. are JSON serialisable.
 *  Values can be restricted to solely their type,
 *  or can also be allowed to be `null` (if the item needs to be able to be deleted).
 */
export interface LocalStorageTypes {
  /* FLN LOCAL STORAGE TYPES */
  readonly arrowPostProjectDraft: ArrowPostJobFormState | null; // We can stop using this once draft support is added
  readonly developersPreference: DevelopersPreference | null;
  readonly endProjectFunnelStep: {
    readonly [bidId: number]: UserReviewStep;
  };
  readonly postProjectObject: ProjectViewProject | null; // We can stop using this once login/signup is in webapp
  readonly projectOverlayThreadComplete: {
    readonly [threadId: number]: boolean;
  };
  readonly projectOverlayFullFlowComplete: boolean;
  readonly webappChats: SavedChatAttributes;
  readonly webappChatDraftMessages: {
    readonly [threadIdentifier: string]: DraftMessage | null;
  };
  readonly webappThreadListMinimise: boolean;
  readonly contestShowSocialShareBanner: {
    readonly [contestId: number]: boolean;
  };
  readonly taskList: TaskListState | null;
  readonly taskListCurrentTaskClean: boolean | null;

  readonly lastSeenPjpAssistant: LastSeenPjpAssistant;
  readonly hireMeDraft: HireMeDraft | null;

  readonly navUpdatesLastReadTime: number | null;

  readonly dashboardMyProjectsUserType: RoleApi | null;

  readonly enterpriseContactFormSubmitted: boolean; // Used on the enterprise landing page

  readonly postJobPageDraft: PostJobPageFormState | null;

  /* TYPES USED FOR TESTING ONLY */
  readonly testProperty: number;
  readonly testName: string | null;
  readonly testProfile: MockProfile;
}
/*
 * The interfaces below are interfaces needed by `chat-box` and `helpers`
 */

export interface DraftMessage {
  readonly text: string;
  readonly lastUpdated: number;
}

export const enum FocusState {
  Header,
  HeaderAndTypingBox,
  Neither,
}

export type SavedChatAttributes = ReadonlyArray<{
  readonly minimised: boolean;
  readonly focus: FocusState;
  readonly width: number;
  readonly height: number;
  readonly threadIdentifier: ThreadIdentifier;
}>;

export interface ThreadIdentifier {
  readonly id: number; // TODO: consider making id possibly undefined and getting rid of isFake
  readonly threadType: ThreadTypeApi;
  readonly context: ThreadContext;
  readonly members: ReadonlyArray<number>;
  readonly isFake: boolean; // TODO: needed?
}

export enum UserReviewStep {
  REVIEW_FORM,
  TIPPING,
  REHIRE,
  VIEW_REVIEW,
  LOADING,
  PRIVATE_NPS,
}

/* Interface used for TESTING ONLY */
interface MockProfile {
  readonly id: number;
  readonly name: string;
}

export interface HireMeDraft {
  readonly budget: number;
  readonly currencyId: number;
  readonly freelancerId: number;
  readonly hireFor: string;
  readonly hoursPerWeek: number;
  readonly message: string;

  // Optional field for backward compatibility.
  readonly confirmed?: boolean;
}

export interface LastSeenPjpAssistant {
  readonly time: number;
}

export interface TaskListState {
  readonly currentTaskClean?: boolean;
  readonly draftTask?: Partial<Task> | Task;
}
